import React, { Component, Suspense, type ReactNode, type ErrorInfo } from 'react';
import { PageSkeleton } from '@/components/skeletons/PageSkeleton';
import { PageTransition } from '@/components/transitions/PageTransition';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Error Boundary with retry for lazy-loaded chunks
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class LazyErrorBoundary extends Component<
  { children: ReactNode; onRetry?: () => void },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('[LazyRoute] Chunk load failed:', error, info);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="text-center space-y-4 max-w-sm">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              Erro ao carregar página
            </h3>
            <p className="text-sm text-muted-foreground">
              Houve um problema ao carregar esta página. Verifique sua conexão e tente novamente.
            </p>
            <Button onClick={this.handleRetry} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface LazyRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
  withTransition?: boolean;
}

export const LazyRoute: React.FC<LazyRouteProps> = ({
  children,
  fallback = <PageSkeleton />,
  withTransition = true,
}) => {
  return (
    <LazyErrorBoundary>
      <Suspense fallback={fallback}>
        {withTransition ? <PageTransition>{children}</PageTransition> : children}
      </Suspense>
    </LazyErrorBoundary>
  );
};
